import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { OctagonX } from "lucide-react";

import { Button } from "./ui/button";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * De noodstop op glas. POST 5, MASTERPLAN 3ax-5.
 *
 * FRANKS EIS VAN 1 SEPTEMBER 2026: "een noodstop die ik niet kan indrukken is
 * er geen." De dienst en de route stonden er al; dit is de knop.
 *
 * HIJ STAAT IN DE ZIJBALK EN NIET ACHTER EEN TABBLAD, en dat is de eis en geen
 * smaak. Een rem die je eerst moet zoeken werkt niet op het moment dat je hem
 * nodig hebt.
 *
 * ER IS EEN ZETTEN EN GEEN OPHEFFEN, ook hier. Eis 3 van 3ax-5 zegt dat de stop
 * onomkeerbaar is zonder Frank, en dat is een ONTBREKEND VERMOGEN: de backend
 * heeft geen DELETE, dus een knop zou nergens heen kunnen. Wat er staat als hij
 * aan is, is de weg terug in woorden - het bestand met de hand weggooien.
 *
 * EN DE REDEN IS VERPLICHT. Wie hem later overweegt op te heffen heeft precies
 * dat veld nodig; een lege reden maakt van opheffen een gok.
 */
export default function Noodstop({ compact = false }) {
  const { t } = useTaal();
  const [aan, setAan] = useState(false);
  const [details, setDetails] = useState({});
  const [vraagt, setVraagt] = useState(false);
  const [reden, setReden] = useState("");
  const [bezig, setBezig] = useState(false);

  const laden = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/noodstop`);
      setAan(Boolean(data.aan));
      setDetails(data.details || {});
    } catch {
      // STIL, EN DAT IS HIER DE JUISTE KANT. Een foutmelding bij elke
      // paginalading omdat de dienst even niet antwoordt, leert niemand iets;
      // de knop blijft gewoon staan en werkt zodra hij weer antwoordt.
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const zetten = async () => {
    if (!reden.trim()) return;
    setBezig(true);
    try {
      const { data } = await axios.post(`${API}/noodstop`, { reden: reden.trim() });
      setAan(true);
      setDetails(data.details || {});
      setVraagt(false);
      setReden("");
      toast.success(t("De noodstop staat aan. Er gebeurt niets meer."));
    } catch {
      toast.error(t("De noodstop kon niet worden gezet."));
    } finally {
      setBezig(false);
    }
  };

  if (aan) {
    return (
      <div
        data-testid="noodstop-actief"
        className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs space-y-1"
      >
        <div className="flex items-center gap-2 font-medium text-destructive">
          <OctagonX className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("Noodstop staat aan")}
        </div>
        {details.reden && <p className="text-muted-foreground">{details.reden}</p>}
        <p className="text-muted-foreground">
          {t("NovaMund voert niets uit. Opheffen doe je zelf, buiten de app om: verwijder het bestand dat hieronder staat.")}
        </p>
        {details.opheffen && (
          <code className="block break-all text-[10px] text-muted-foreground">
            {details.opheffen}
          </code>
        )}
      </div>
    );
  }

  if (!vraagt) {
    return (
      <Button
        variant="ghost"
        onClick={() => setVraagt(true)}
        data-testid="noodstop-knop"
        className="w-full justify-start gap-2 h-9 text-destructive hover:text-destructive"
      >
        <OctagonX className="h-4 w-4" />
        {!compact && <span>{t("Noodstop")}</span>}
      </Button>
    );
  }

  return (
    <div data-testid="noodstop-vraag" className="space-y-2 rounded-md border p-2">
      <p className="text-xs text-muted-foreground">
        {t("Dit stopt alles wat NovaMund uitvoert. Je kunt het daarna alleen zelf weer aanzetten, buiten de app om.")}
      </p>
      <input
        data-testid="noodstop-reden"
        value={reden}
        onChange={(e) => setReden(e.target.value)}
        placeholder={t("Waarom stop je?")}
        className="w-full rounded border bg-background px-2 py-1 text-xs"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="destructive"
          disabled={!reden.trim() || bezig}
          onClick={zetten}
          data-testid="noodstop-bevestig"
        >
          {t("Stop alles")}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setVraagt(false)}>
          {t("Laat maar")}
        </Button>
      </div>
    </div>
  );
}
